import classNames from 'classnames'
import { useEffect, useState } from 'react'

import { commonIcons } from '@apps-common/config/icons'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { imageUrlReplace } from '@apps-common/helpers/url-replace'
import { AnimatePresence, motion } from 'framer-motion'

interface IProps {
  src: string | undefined
  alt: string
  containerClasses?: string
  imageClasses: string
  errorComponent?: any
  errorIconClasses?: string
  ignoreContainerOnError?: boolean
}

export const ImageComponent: React.FC<IProps> = ({
  src,
  alt,
  containerClasses,
  imageClasses,
  errorComponent,
  errorIconClasses,
  ignoreContainerOnError
}) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [brokenImg, setBrokenImg] = useState<boolean>(false)
  const resolvedSrc = imageUrlReplace(src)

  useEffect(() => {
    if (!resolvedSrc) {
      setLoading(false)
      setBrokenImg(true)
      return
    }

    let cancelled = false
    const preloadImage = new window.Image()

    setLoading(true)
    setBrokenImg(false)

    preloadImage.onload = () => {
      if (!cancelled) {
        setLoading(false)
      }
    }

    preloadImage.onerror = () => {
      if (!cancelled) {
        setLoading(false)
        setBrokenImg(true)
      }
    }

    preloadImage.src = resolvedSrc

    if (preloadImage.complete) {
      setLoading(false)
    }

    return () => {
      cancelled = true
    }
  }, [resolvedSrc])

  const ImageIcon = commonIcons.image

  return (
    <AnimatePresence>
      {containerClasses && (
        <>
          {ignoreContainerOnError && (
            <>
              {loading && resolvedSrc && (
                <div className={containerClasses}>
                  <motion.div {...motionFadeIn(0.2)} className={classNames(imageClasses, 'animate-pulse bg-skeleton')} />
                </div>
              )}
              {!loading && !brokenImg && resolvedSrc && (
                <div className={containerClasses}>
                  <motion.img {...motionFadeIn(0.2)} src={resolvedSrc} alt={alt} className={classNames(imageClasses)} />
                </div>
              )}
              {(brokenImg || !resolvedSrc) && (
                <>
                  {errorComponent ? (
                    <motion.div {...motionFadeIn(0.2)} className={errorIconClasses}>
                      {errorComponent}
                    </motion.div>
                  ) : (
                    <motion.div {...motionFadeIn(0.2)} className='flex items-center justify-center'>
                      <ImageIcon className={classNames(errorIconClasses || 'w-[80%] h-[80%]', 'opacity-50')} />
                    </motion.div>
                  )}
                </>
              )}
            </>
          )}
          {!ignoreContainerOnError && (
            <div className={containerClasses}>
              {loading && resolvedSrc && (
                <motion.div {...motionFadeIn(0.2)} className={classNames(imageClasses, 'animate-pulse bg-skeleton')} />
              )}
              {!loading && !brokenImg && resolvedSrc && (
                <motion.img {...motionFadeIn(0.2)} src={resolvedSrc} alt={alt} className={classNames(imageClasses)} />
              )}
              {(brokenImg || !resolvedSrc) && (
                <>
                  {errorComponent ? (
                    <motion.div {...motionFadeIn(0.2)} className={classNames(errorIconClasses || 'w-[60%] h-[60%]', 'opacity-30')}>
                      {errorComponent}
                    </motion.div>
                  ) : (
                    <motion.div {...motionFadeIn(0.2)} className='w-full h-full flex items-center justify-center'>
                      <ImageIcon className={classNames(errorIconClasses || 'w-[80%] h-[80%]', 'opacity-30')} />
                    </motion.div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
      {!containerClasses && (
        <>
          {loading && resolvedSrc && (
            <motion.div {...motionFadeIn(0.2)} className={classNames(imageClasses, 'animate-pulse bg-skeleton')} />
          )}
          {!loading && !brokenImg && resolvedSrc && (
            <motion.img {...motionFadeIn(0.2)} src={resolvedSrc} alt={alt} className={classNames(imageClasses)} />
          )}
          {(brokenImg || !resolvedSrc) && (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {errorComponent ? (
                <motion.div {...motionFadeIn(0.2)} className={classNames(errorIconClasses || 'w-[60%] h-[60%]', 'opacity-30')}>
                  {errorComponent}
                </motion.div>
              ) : (
                <motion.div {...motionFadeIn(0.2)}>
                  <ImageIcon className={classNames(errorIconClasses || 'w-[60%] h-[60%]', 'opacity-30')} />
                </motion.div>
              )}
            </>
          )}
        </>
      )}
    </AnimatePresence>
  )
}
